import { Redirect } from "wouter";
import { useAdminRole, type AdminRole } from "@/hooks/use-admin-role";

/**
 * Guards admin pages on the client. Calls /api/admin/check (which requires
 * the admin_session cookie); on failure redirects to /admin-login. Until the
 * check resolves we render nothing — this prevents the protected page from
 * flashing for half a second to non-admin visitors.
 *
 * When `allowedRoles` is provided, a logged-in admin whose role is not in the
 * list is redirected to the area they can access (moderators → Messages
 * Safety). Server-side enforcement is independent of this guard.
 */
export function AdminGate({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: AdminRole[];
}) {
  const { status, role } = useAdminRole();

  if (status === "checking") return null;
  if (status === "denied") return <Redirect to="/admin-login" />;

  if (allowedRoles && role && !allowedRoles.includes(role)) {
    // Send the user to the home they're allowed to see instead of a dead end.
    const fallback = role === "moderator" ? "/admin/messages-safety" : "/admin";
    return <Redirect to={fallback} />;
  }

  return <>{children}</>;
}
